/** Customer Home — Executive Minimal.
 *  Structure per PREMIUM ENTERPRISE UI SIMPLIFICATION spec:
 *    Welcome → Technology estate → Needs attention → Today → Quick actions →
 *    Performance → Recent activity.
 *  Zero new backend — reuses /api/dashboard/executive + /api/audit-logs. */
import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import PageHeader from "@/components/app/PageHeader";
import Section from "@/components/app/Section";
import MetricRow from "@/components/app/MetricRow";
import YourOneProfile from "@/components/app/YourOneProfile";
import {
  Rocket, Undo2, UserPlus, UserMinus, LifeBuoy, ShoppingCart,
  ArrowRight, CheckCircle2, Clock,
} from "lucide-react";
import { CustomerActionsPanel } from "@/pages/customer/CustomerActions";
import AskSuntronixAI from "@/components/askai/AskSuntronixAI";
import AlertsStrip from "@/components/alerts/AlertsStrip";

function greeting() {
  const h = new Date().getHours();
  if (h < 5)  return "Good night";
  if (h < 12) return "Good morning";
  if (h < 17) return "Good afternoon";
  return "Good evening";
}

const SEV_CLASS = {
  critical: "border-[color:var(--st-critical-soft)] bg-[color:var(--st-critical-soft)]",
  high:     "border-[color:var(--st-warn-soft)] bg-[color:var(--st-warn-soft)]",
  medium:   "border-[color:var(--st-info-soft)] bg-[color:var(--st-info-soft)]",
  low:      "border-[color:var(--st-border)] bg-[color:var(--st-surface)]",
};

export default function CustomerHome() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [activity, setActivity] = useState([]);
  const [approvals, setApprovals] = useState(0);

  useEffect(() => {
    let cancel = false;
    Promise.all([
      api.get("/dashboard/executive"),
      api.get("/audit-logs", { params: { limit: 6 } }).catch(() => ({ data: { items: [] } })),
      api.get("/approvals", { params: { status: "pending", limit: 100 } }).catch(() => ({ data: { items: [], total: 0 } })),
    ]).then(([exec, audit, appr]) => {
      if (cancel) return;
      setData(exec.data);
      setActivity(audit.data.items || []);
      setApprovals(appr.data.total ?? (appr.data.items || []).length);
    }).finally(() => !cancel && setLoading(false));
    return () => { cancel = true; };
  }, []);

  const kpis = data?.kpi || {};
  const activityCounts = data?.lifecycle_activity || {};
  const attention = (data?.attention || []).filter((a) => (a.count || 0) > 0);
  const firstName = (user?.name || "there").split(" ")[0];
  const readOnly = ["customer_viewer", "customer_auditor"].includes(user?.role);
  const tenantName = data?.tenant_name || "your organisation";

  const estate = [
    { label: "Assets",    value: fmt(data?.total_assets), onClick: () => navigate("/assets"),                              testId: "cx-estate-assets" },
    { label: "Employees", value: fmt(data?.employees),    onClick: () => navigate("/employees"),                           testId: "cx-estate-employees" },
    { label: "Countries", value: fmt(data?.countries),                                                                     testId: "cx-estate-countries" },
    { label: "In use",    value: fmt(kpis.in_use),        onClick: () => navigate("/assets?lifecycle_state=in_use"),       testId: "cx-estate-in-use" },
    { label: "Available", value: fmt(kpis.available),     onClick: () => navigate("/assets?lifecycle_state=in_warehouse"), testId: "cx-estate-available" },
    { label: "In transit",value: fmt(kpis.in_transit),    onClick: () => navigate("/shipments"),                           testId: "cx-estate-transit" },
  ];

  const today = [
    { label: "Deliveries",   value: fmt(activityCounts.received),   to: "/shipments" },
    { label: "Pickups",      value: fmt(activityCounts.recovered),  to: "/pickups" },
    { label: "Joiners",      value: fmt(kpis.joiners  ?? 0),        to: "/employees" },
    { label: "Service cases",value: fmt(kpis.repair),               to: "/service" },
  ];

  const perf = [
    { label: "SLA",          value: pct(data?.performance?.sla)          },
    { label: "Recovery",     value: pct(data?.performance?.recovery)     },
    { label: "Data quality", value: pct(data?.performance?.data_quality) },
  ];

  return (
    <div className="space-y-6 max-w-[1280px]" data-testid="customer-home">
      {/* WELCOME — compact, non-marketing */}
      <PageHeader
        eyebrow="Home"
        title={<><span className="font-normal text-[color:var(--st-text-muted)]">{greeting()},</span> {firstName}</>}
        context={<>{tenantName} · {attention.length === 0
          ? "your technology estate is operating normally."
          : <>{attention.length} item{attention.length === 1 ? "" : "s"} require your attention today.</>}
        </>}
        actions={
          <span className="text-[11px] text-[color:var(--st-text-subtle)] hidden md:inline-flex items-center gap-1">
            <Clock className="h-3 w-3" strokeWidth={1.5} />
            Last sync {new Date().toLocaleString("en-GB", { day: "2-digit", month: "short", hour: "2-digit", minute: "2-digit" })}
          </span>
        }
        testId="cx-header"
      />

      <AskSuntronixAI compact />
      <AlertsStrip />

      <CustomerActionsPanel />

      {/* PENDING APPROVALS — highest-priority action, surfaced first */}
      {approvals > 0 && (
        <button
          data-testid="cx-approvals-banner"
          onClick={() => navigate("/approvals")}
          className="w-full text-left border rounded-md px-5 py-4 flex items-center gap-4 transition-colors border-[color:var(--st-warn-soft)] bg-[color:var(--st-warn-soft)] hover:brightness-[0.98]">
          <div className="h-10 w-10 rounded-full bg-[color:var(--st-surface)] grid place-items-center shrink-0">
            <CheckCircle2 className="h-5 w-5 text-[color:var(--st-warn)]" strokeWidth={1.6} />
          </div>
          <div className="flex-1 min-w-0">
            <div className="text-sm font-semibold text-[color:var(--st-text)]">
              {approvals} approval{approvals === 1 ? "" : "s"} waiting for you
            </div>
            <div className="text-[13px] text-[color:var(--st-text-muted)] mt-0.5">
              Review and decide so your team's requests can move forward.
            </div>
          </div>
          <span className="st-btn st-btn-primary st-btn-sm shrink-0">
            Review approvals <ArrowRight className="h-3.5 w-3.5" strokeWidth={1.6} />
          </span>
        </button>
      )}

      {/* QUICK ACTIONS — action-first, at the top. Hidden for read-only roles. */}
      {!readOnly && (
      <Section title="What would you like to do?" testId="cx-actions">
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-2">
          <QA icon={Rocket}       label="Deploy device"     onClick={() => navigate("/deploy")}        primary testId="cx-action-deploy" />
          <QA icon={Undo2}        label="Recover device"    onClick={() => navigate("/pickups")}       testId="cx-action-recover" />
          <QA icon={UserPlus}     label="Onboard employee"  onClick={() => navigate("/employees")}     testId="cx-action-onboard" />
          <QA icon={UserMinus}    label="Offboard employee" onClick={() => navigate("/offboarding")}   testId="cx-action-offboard" />
          <QA icon={LifeBuoy}     label="Request support"   onClick={() => navigate("/services")}      testId="cx-action-support" />
          <QA icon={ShoppingCart} label="Order technology"  onClick={() => navigate("/marketplace")}   testId="cx-action-order" />
        </div>
      </Section>
      )}

      {/* YOUR ONE PROFILE — customer-facing playbook subset */}
      <YourOneProfile />

      {/* TECHNOLOGY ESTATE — one structured panel of numbers */}
      <Section title="Technology estate" hint="What we manage for you" testId="cx-estate">
        {loading
          ? <div className="st-skeleton h-24 rounded-md" />
          : <MetricRow items={estate} testId="cx-estate-row" />}
      </Section>

      {/* NEEDS ATTENTION — the most important section */}
      <Section
        title="Needs your attention"
        hint="Only exceptions are surfaced here."
        action={attention.length > 0 && (
          <button className="st-btn st-btn-ghost st-btn-sm" onClick={() => navigate("/exceptions")}>
            View all <ArrowRight className="h-3.5 w-3.5" strokeWidth={1.6} />
          </button>
        )}
        testId="cx-attention">
        {loading ? (
          <div className="grid grid-cols-1 md:grid-cols-3 gap-2">
            {[0,1,2].map((i) => <div key={i} className="st-skeleton h-14" />)}
          </div>
        ) : attention.length === 0 ? (
          <div className="st-panel p-6 flex items-start gap-4" data-testid="cx-calm-mode">
            <CheckCircle2 className="h-5 w-5 text-[color:var(--st-success)] shrink-0 mt-0.5" strokeWidth={1.6} />
            <div>
              <div className="text-sm font-medium text-[color:var(--st-text)]">Everything looks good.</div>
              <div className="text-[13px] text-[color:var(--st-text-muted)] mt-1">
                No critical action requires your attention. {fmt(data?.total_assets || 0)} technology assets are being managed
                across {data?.countries || 0} countries.
              </div>
            </div>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-2" data-testid="cx-priorities">
            {attention.map((a) => (
              <button key={a.kind + a.label}
                data-testid={`cx-priority-${a.kind}`}
                onClick={() => navigate(a.link || "/exceptions")}
                className={`text-left border rounded-md px-4 py-3 flex items-center gap-3 transition-colors ${SEV_CLASS[a.severity] || SEV_CLASS.medium}`}>
                <div className="flex-1 min-w-0">
                  <div className="text-sm font-medium text-[color:var(--st-text)]">{friendlyLabel(a.label)}</div>
                  <div className="st-overline mt-0.5">{a.severity}</div>
                </div>
                <div className="st-metric st-metric-md">{a.count}</div>
                <ArrowRight className="h-4 w-4 text-[color:var(--st-text-subtle)] shrink-0" strokeWidth={1.5} />
              </button>
            ))}
          </div>
        )}
      </Section>

      {/* TODAY */}
      <Section title="Today" hint="Operational events happening right now." testId="cx-today">
        <MetricRow items={today.map((t) => ({ ...t, onClick: () => navigate(t.to), testId: `cx-today-${t.label.toLowerCase().replace(/\s+/g,"-")}` }))} />
      </Section>

      {/* PERFORMANCE — three restrained percentages, no charts */}
      <Section title="Performance" hint="Detailed analysis lives in Insights." testId="cx-performance"
        action={<button className="st-btn st-btn-ghost st-btn-sm" onClick={() => navigate("/reports")}>Insights <ArrowRight className="h-3.5 w-3.5" strokeWidth={1.6}/></button>}>
        <MetricRow items={perf} />
      </Section>

      {/* RECENT ACTIVITY */}
      <Section title="Recent activity" hint="What Suntronix is doing for you." testId="cx-activity">
        <div className="st-panel overflow-hidden">
          {activity.length === 0 ? (
            <div className="p-6 text-[13px] text-[color:var(--st-text-muted)]">
              No recent activity yet — as requests and shipments are processed, updates will appear here.
            </div>
          ) : (
            <ul className="divide-y divide-[color:var(--st-border)]">
              {activity.slice(0, 6).map((a) => (
                <li key={a.id} className="px-4 py-2.5 text-[13px] flex items-baseline gap-3">
                  <span className="text-[color:var(--st-text)]">{friendlyActivity(a)}</span>
                  <span className="text-[11px] text-[color:var(--st-text-subtle)] ml-auto">{relativeTime(a.timestamp)}</span>
                </li>
              ))}
            </ul>
          )}
        </div>
      </Section>
    </div>
  );
}

/* ---------- helpers ---------- */
function QA({ icon: Icon, label, onClick, primary, testId }) {
  return (
    <button type="button" data-testid={testId} onClick={onClick}
      className={"st-btn " + (primary ? "st-btn-primary" : "st-btn-secondary") + " h-11 justify-start px-4"}>
      <Icon className="h-4 w-4" strokeWidth={1.6} />
      <span className="truncate">{label}</span>
    </button>
  );
}
function fmt(n) { if (n == null) return "—"; return Number(n).toLocaleString(); }
function pct(n) { if (n == null) return "—"; return `${Number(n).toFixed(1)}%`; }
function friendlyLabel(label) {
  if (!label) return "";
  return label
    .replace(/Reverse pickup(?:s)?/gi, "Devices to recover")
    .replace(/ITADs?/g, "Disposition")
    .replace(/SLA breach(?:es)?/gi, "Overdue with Suntronix")
    .replace(/PODs?/gi, "Delivery proof")
    .replace(/RMAs?/gi, "Warranty replacement");
}
function friendlyActivity(a) {
  const who = a.actor_email ? a.actor_email.split("@")[0] : "Suntronix";
  const action = (a.action || "").replace(/_/g, " ");
  const entity = (a.entity_type || "").replace(/_/g, " ");
  return `${who} ${action}${entity ? " " + entity : ""}${a.entity_id ? ` (${String(a.entity_id).slice(0, 6)})` : ""}`;
}
function relativeTime(ts) {
  if (!ts) return "";
  const diff = (Date.now() - new Date(ts).getTime()) / 1000;
  if (diff < 60)    return "just now";
  if (diff < 3600)  return `${Math.round(diff / 60)} min ago`;
  if (diff < 86400) return `${Math.round(diff / 3600)} hours ago`;
  return `${Math.round(diff / 86400)} days ago`;
}
